/**
 * Canonical question catalog for the Property Insurance Quote flow.
 *
 * Each question has a stable `id`, a `section` it belongs to, and an optional
 * `showIf` predicate that filters on classifier + in-form answers. The schema
 * engine walks this list in order and emits only the questions whose section
 * and showIf both pass.
 *
 * PDF field mappings (canonical id → pdfFieldName per template) are authored
 * separately in P-7 as part of the generation service — the form itself
 * doesn't need them.
 */
// --- helpers -----------------------------------------------------------
const yesNoOptions = [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
];
// --- Section 01 — Applicant --------------------------------------------
const applicant = [
    {
        id: 'applicant.legalName',
        section: 'applicant',
        label: 'Legal name of applicant',
        type: 'text',
        required: true,
        placeholder: 'e.g. Sylvan Circle Apartments, LLC',
    },
    {
        id: 'applicant.entityType',
        section: 'applicant',
        label: 'Entity type',
        type: 'radio',
        required: true,
        options: [
            { value: 'corporation', label: 'Corporation' },
            { value: 'llc', label: 'LLC' },
            { value: 'partnership', label: 'Partnership' },
            { value: 'individual', label: 'Individual' },
            { value: 'not-for-profit', label: 'Not for profit' },
        ],
    },
    {
        id: 'applicant.fein',
        section: 'applicant',
        label: 'Federal EIN',
        type: 'text',
        placeholder: '00-0000000',
    },
    {
        id: 'applicant.yearsInBusiness',
        section: 'applicant',
        label: 'Years in business',
        type: 'number',
    },
    {
        id: 'applicant.mailingStreet',
        section: 'applicant',
        label: 'Mailing address',
        type: 'text',
        required: true,
    },
    {
        id: 'applicant.mailingCity',
        section: 'applicant',
        label: 'City',
        type: 'text',
        required: true,
    },
    {
        id: 'applicant.mailingState',
        section: 'applicant',
        label: 'State',
        type: 'state',
        required: true,
    },
    {
        id: 'applicant.mailingZip',
        section: 'applicant',
        label: 'ZIP',
        type: 'text',
        required: true,
        placeholder: '00000',
    },
    {
        id: 'applicant.website',
        section: 'applicant',
        label: 'Website',
        type: 'text',
        placeholder: 'https://',
    },
    {
        id: 'applicant.contactName',
        section: 'applicant',
        label: 'Primary contact name',
        type: 'text',
        required: true,
    },
    {
        id: 'applicant.contactEmail',
        section: 'applicant',
        label: 'Primary contact email',
        type: 'email',
        required: true,
    },
    {
        id: 'applicant.contactPhone',
        section: 'applicant',
        label: 'Primary contact phone',
        type: 'phone',
        required: true,
    },
    {
        id: 'applicant.hasPropertyManager',
        section: 'applicant',
        label: 'Does the applicant use a property manager for this location?',
        type: 'yesno',
        options: yesNoOptions,
    },
    {
        id: 'applicant.propertyManagerName',
        section: 'applicant',
        label: 'Property management company name',
        type: 'text',
        showIf: (_c, a) => a['applicant.hasPropertyManager'] === 'yes',
    },
    {
        id: 'applicant.priorExperience',
        section: 'applicant',
        label: 'Years of prior habitational ownership/management experience',
        type: 'number',
    },
];
// --- Section 02 — Building ---------------------------------------------
const building = [
    {
        id: 'building.locationStreet',
        section: 'building',
        label: 'Building street address',
        type: 'text',
        required: true,
    },
    {
        id: 'building.locationCity',
        section: 'building',
        label: 'City',
        type: 'text',
        required: true,
    },
    {
        id: 'building.locationCounty',
        section: 'building',
        label: 'County',
        type: 'text',
    },
    {
        id: 'building.yearBuilt',
        section: 'building',
        label: 'Year built',
        type: 'year',
        required: true,
    },
    {
        id: 'building.numBuildings',
        section: 'building',
        label: 'Number of buildings',
        type: 'number',
        required: true,
    },
    {
        id: 'building.numStories',
        section: 'building',
        label: 'Number of stories',
        type: 'number',
        required: true,
    },
    {
        id: 'building.numUnits',
        section: 'building',
        label: 'Number of units',
        type: 'number',
        required: true,
    },
    {
        id: 'building.totalSqFt',
        section: 'building',
        label: 'Total square footage',
        type: 'number',
        required: true,
    },
    {
        id: 'building.replacementValue',
        section: 'building',
        label: 'Building replacement value',
        type: 'currency',
    },
    {
        id: 'building.annualIncome',
        section: 'building',
        label: 'Annual rental income',
        type: 'currency',
    },
    {
        id: 'building.yearsOwned',
        section: 'building',
        label: 'Years owned',
        type: 'number',
    },
    {
        id: 'building.constructionType',
        section: 'building',
        label: 'ISO construction type',
        type: 'select',
        required: true,
        options: [
            { value: 'frame', label: 'Frame (ISO-1)' },
            { value: 'frame-over-podium', label: 'Frame over podium (ISO-1)' },
            { value: 'joisted-masonry', label: 'Joisted masonry (ISO-2)' },
            { value: 'noncombustible', label: 'Noncombustible (ISO-3)' },
            { value: 'mnc', label: 'MNC (ISO-4)' },
            { value: 'mod-fire-resistive', label: 'Modified fire-resistive (ISO-5)' },
            { value: 'fire-resistive', label: 'Fire-resistive (ISO-6)' },
        ],
    },
    {
        id: 'building.wiringType',
        section: 'building',
        label: 'Electrical wiring',
        type: 'radio',
        options: [
            { value: 'copper', label: 'Copper' },
            { value: 'aluminum', label: 'Aluminum' },
        ],
    },
    {
        id: 'building.aluminumUpdate',
        section: 'building',
        label: 'Aluminum update method',
        type: 'radio',
        options: [
            { value: 'copalum', label: 'CopAlum' },
            { value: 'pigtailed', label: 'Pigtailed' },
            { value: 'co-alr', label: 'CO/ALR' },
        ],
        showIf: (_c, a) => a['building.wiringType'] === 'aluminum',
    },
    {
        id: 'building.fusesOrBreakers',
        section: 'building',
        label: 'Fuses or circuit breakers?',
        type: 'radio',
        options: [
            { value: 'breakers', label: 'Circuit breakers' },
            { value: 'fuses', label: 'Fuses' },
        ],
    },
    {
        id: 'building.federalPacific',
        section: 'building',
        label: 'Any Federal Pacific Stab-Lok or Zinsco panels?',
        type: 'yesno',
        options: yesNoOptions,
    },
    {
        id: 'building.eifsSiding',
        section: 'building',
        label: 'Any EIFS or DEFS siding?',
        type: 'yesno',
        options: yesNoOptions,
    },
    {
        id: 'building.roofUpdateYear',
        section: 'building',
        label: 'Roof last updated (year)',
        type: 'year',
    },
    {
        id: 'building.hvacUpdateYear',
        section: 'building',
        label: 'HVAC last updated (year)',
        type: 'year',
    },
    {
        id: 'building.electricalUpdateYear',
        section: 'building',
        label: 'Electrical last updated (year)',
        type: 'year',
    },
    {
        id: 'building.plumbingUpdateYear',
        section: 'building',
        label: 'Plumbing last updated (year)',
        type: 'year',
    },
];
// --- Section 03 — Occupancy --------------------------------------------
const occupancy = [
    {
        id: 'occupancy.occupancyPct',
        section: 'occupancy',
        label: 'Current occupancy rate',
        type: 'percent',
        required: true,
    },
    {
        id: 'occupancy.marketRatePct',
        section: 'occupancy',
        label: 'Market-rate units (% of total)',
        type: 'percent',
    },
    // Subsidized — only if classifier.features includes subsidized
    {
        id: 'occupancy.subsidizedPct',
        section: 'occupancy',
        label: 'Subsidized units (% of total)',
        type: 'percent',
        showIf: (c) => c.features.includes('subsidized'),
    },
    {
        id: 'occupancy.subsidizedType',
        section: 'occupancy',
        label: 'Subsidy type',
        type: 'multiselect',
        options: [
            { value: 'hud', label: 'HUD' },
            { value: 'voucher', label: 'Voucher' },
            { value: 'state', label: 'State' },
            { value: 'section-8', label: 'Section 8' },
            { value: 'section-42', label: 'Section 42' },
        ],
        showIf: (c) => c.features.includes('subsidized'),
    },
    // Senior — only if propertyType senior
    {
        id: 'occupancy.seniorPct',
        section: 'occupancy',
        label: 'Senior residents (% of total)',
        type: 'percent',
        showIf: (c) => c.propertyType === 'senior',
    },
    {
        id: 'occupancy.callButtons',
        section: 'occupancy',
        label: 'Units equipped with call buttons or pull cords?',
        type: 'yesno',
        options: yesNoOptions,
        showIf: (c) => c.propertyType === 'senior',
    },
    {
        id: 'occupancy.elderCare',
        section: 'occupancy',
        label: 'Elder care or health services provided?',
        type: 'yesno',
        options: yesNoOptions,
        showIf: (c) => c.propertyType === 'senior',
    },
    {
        id: 'occupancy.mealsProvided',
        section: 'occupancy',
        label: 'Meals provided for tenants?',
        type: 'yesno',
        options: yesNoOptions,
        showIf: (c) => c.propertyType === 'senior',
    },
    {
        id: 'occupancy.assistedLiving',
        section: 'occupancy',
        label: 'Assisted living?',
        type: 'yesno',
        options: yesNoOptions,
        showIf: (c) => c.propertyType === 'senior',
    },
    // Student — only if propertyType student
    {
        id: 'occupancy.studentPct',
        section: 'occupancy',
        label: 'Students (% of residents)',
        type: 'percent',
        showIf: (c) => c.propertyType === 'student',
    },
    {
        id: 'occupancy.studentType',
        section: 'occupancy',
        label: 'Student type',
        type: 'radio',
        options: [
            { value: 'undergraduate', label: 'Undergraduate' },
            { value: 'graduate', label: 'Graduate' },
        ],
        showIf: (c) => c.propertyType === 'student',
    },
    {
        id: 'occupancy.collegeAssociated',
        section: 'occupancy',
        label: 'Associated with a college or university?',
        type: 'yesno',
        options: yesNoOptions,
        showIf: (c) => c.propertyType === 'student',
    },
    // Condo-specific
    {
        id: 'occupancy.condoBuilderOnBoard',
        section: 'occupancy',
        label: 'Does the builder/developer own units or sit on the board?',
        type: 'yesno',
        options: yesNoOptions,
        showIf: (c) => c.propertyType === 'condo',
    },
    {
        id: 'occupancy.rentalUnitPct',
        section: 'occupancy',
        label: 'Rental units (% of total)',
        type: 'percent',
        showIf: (c) => c.propertyType === 'condo',
    },
];
// --- Section 04 — Life Safety & Fire Protection ------------------------
const lifeSafety = [
    {
        id: 'lifeSafety.sprinkleredPct',
        section: 'lifeSafety',
        label: 'Percentage of building sprinklered',
        type: 'percent',
    },
    {
        id: 'lifeSafety.sprinkledAreas',
        section: 'lifeSafety',
        label: 'Which areas are sprinklered?',
        type: 'multiselect',
        options: [
            { value: 'common', label: 'Common areas' },
            { value: 'trash-chutes', label: 'Trash chutes' },
            { value: 'all', label: 'All' },
        ],
    },
    {
        id: 'lifeSafety.sprinklerInspected',
        section: 'lifeSafety',
        label: 'Sprinkler systems inspected annually with 3+ years of records?',
        type: 'yesno',
        options: yesNoOptions,
    },
    {
        id: 'lifeSafety.smokeDetectors',
        section: 'lifeSafety',
        label: 'Smoke detectors in all units and corridors?',
        type: 'yesno',
        options: yesNoOptions,
    },
    {
        id: 'lifeSafety.smokeDetectorType',
        section: 'lifeSafety',
        label: 'Smoke detector type',
        type: 'radio',
        options: [
            { value: 'hardwired-backup', label: 'Hardwired with battery backup' },
            { value: 'hardwired', label: 'Hardwired only' },
            { value: 'battery', label: 'Battery only' },
        ],
        showIf: (_c, a) => a['lifeSafety.smokeDetectors'] === 'yes',
    },
    {
        id: 'lifeSafety.coDetectors',
        section: 'lifeSafety',
        label: 'Carbon monoxide detectors in all units and corridors?',
        type: 'yesno',
        options: yesNoOptions,
    },
    {
        id: 'lifeSafety.fireAlarms',
        section: 'lifeSafety',
        label: 'Fire alarms present?',
        type: 'yesno',
        options: yesNoOptions,
    },
    {
        id: 'lifeSafety.centralStation',
        section: 'lifeSafety',
        label: 'Central-station monitored?',
        type: 'yesno',
        options: yesNoOptions,
        showIf: (_c, a) => a['lifeSafety.fireAlarms'] === 'yes',
    },
    {
        id: 'lifeSafety.fireExtinguishers',
        section: 'lifeSafety',
        label: 'Fire extinguishers present?',
        type: 'yesno',
        options: yesNoOptions,
    },
    {
        id: 'lifeSafety.extinguisherLocations',
        section: 'lifeSafety',
        label: 'Extinguisher locations',
        type: 'multiselect',
        options: [
            { value: 'each-unit', label: 'Each unit' },
            { value: 'hallways', label: 'Hallways' },
            { value: 'common', label: 'Common areas' },
        ],
        showIf: (_c, a) => a['lifeSafety.fireExtinguishers'] === 'yes',
    },
    {
        id: 'lifeSafety.emergencyExitsMarked',
        section: 'lifeSafety',
        label: 'Emergency exits marked and illuminated?',
        type: 'yesno',
        options: yesNoOptions,
    },
    {
        id: 'lifeSafety.emergencyLighting',
        section: 'lifeSafety',
        label: 'Emergency lighting installed?',
        type: 'yesno',
        options: yesNoOptions,
    },
    {
        id: 'lifeSafety.twoEgressPerFloor',
        section: 'lifeSafety',
        label: 'Two means of egress from each floor?',
        type: 'yesno',
        options: yesNoOptions,
    },
    {
        id: 'lifeSafety.hasElevator',
        section: 'lifeSafety',
        label: 'Elevator(s) on premises?',
        type: 'yesno',
        options: yesNoOptions,
    },
    {
        id: 'lifeSafety.elevatorServiced',
        section: 'lifeSafety',
        label: 'Maintained under service contract?',
        type: 'yesno',
        options: yesNoOptions,
        showIf: (_c, a) => a['lifeSafety.hasElevator'] === 'yes',
    },
    {
        id: 'lifeSafety.balconies',
        section: 'lifeSafety',
        label: 'Outside balconies or decks?',
        type: 'yesno',
        options: yesNoOptions,
    },
    {
        id: 'lifeSafety.grillsAllowed',
        section: 'lifeSafety',
        label: 'Grills or cooking allowed on balconies/decks?',
        type: 'yesno',
        options: yesNoOptions,
        showIf: (_c, a) => a['lifeSafety.balconies'] === 'yes',
    },
];
// --- Section 05 — Security ---------------------------------------------
const security = [
    {
        id: 'security.fenced',
        section: 'security',
        label: 'Property fenced or gated?',
        type: 'yesno',
        options: yesNoOptions,
    },
    {
        id: 'security.controlledAccess',
        section: 'security',
        label: 'Controlled access?',
        type: 'yesno',
        options: yesNoOptions,
    },
    {
        id: 'security.accessMethod',
        section: 'security',
        label: 'Access method',
        type: 'multiselect',
        options: [
            { value: 'key-fob', label: 'Key fob' },
            { value: 'physical-key', label: 'Physical key' },
            { value: 'intercom', label: 'Intercom' },
            { value: 'security-code', label: 'Security code' },
            { value: 'doorman', label: 'Doorman' },
        ],
        showIf: (_c, a) => a['security.controlledAccess'] === 'yes',
    },
    {
        id: 'security.cameras',
        section: 'security',
        label: 'Security cameras on premises?',
        type: 'yesno',
        options: yesNoOptions,
    },
    {
        id: 'security.deadbolts',
        section: 'security',
        label: 'Deadbolts on unit doors?',
        type: 'yesno',
        options: yesNoOptions,
    },
    {
        id: 'security.reKeyedOnTurnover',
        section: 'security',
        label: 'Units re-keyed on tenant change?',
        type: 'yesno',
        options: yesNoOptions,
    },
    {
        id: 'security.hasSecurityService',
        section: 'security',
        label: 'Security service or guards on premises?',
        type: 'yesno',
        options: yesNoOptions,
    },
    {
        id: 'security.securityType',
        section: 'security',
        label: 'Security arrangement',
        type: 'radio',
        options: [
            { value: 'employee-armed', label: 'Employee, armed' },
            { value: 'employee-unarmed', label: 'Employee, unarmed' },
            { value: 'subcontracted', label: 'Subcontracted' },
        ],
        showIf: (_c, a) => a['security.hasSecurityService'] === 'yes',
    },
    {
        id: 'security.tenantsScreened',
        section: 'security',
        label: 'Tenants screened prior to leasing?',
        type: 'yesno',
        options: yesNoOptions,
    },
    {
        id: 'security.tenantScreeningType',
        section: 'security',
        label: 'Tenant screening includes',
        type: 'multiselect',
        options: [
            { value: 'criminal', label: 'Criminal' },
            { value: 'credit', label: 'Credit' },
            { value: 'references', label: 'References' },
        ],
        showIf: (_c, a) => a['security.tenantsScreened'] === 'yes',
    },
    {
        id: 'security.employeesScreened',
        section: 'security',
        label: 'Employees screened prior to hiring?',
        type: 'yesno',
        options: yesNoOptions,
    },
    {
        id: 'security.formalEvictionPolicy',
        section: 'security',
        label: 'Formal eviction policy in place?',
        type: 'yesno',
        options: yesNoOptions,
    },
    {
        id: 'security.petPolicy',
        section: 'security',
        label: 'Pet policy in place?',
        type: 'yesno',
        options: yesNoOptions,
    },
    {
        id: 'security.petPolicyDescr',
        section: 'security',
        label: 'Describe pet policy',
        type: 'textarea',
        placeholder: 'Breed restrictions, weight limits, deposits, etc.',
        showIf: (_c, a) => a['security.petPolicy'] === 'yes',
    },
];
// --- Section 06 — Maintenance & Operations -----------------------------
const maintenance = [
    {
        id: 'maintenance.hasStaff',
        section: 'maintenance',
        label: 'Maintenance staff on-site?',
        type: 'yesno',
        options: yesNoOptions,
    },
    {
        id: 'maintenance.staffStatus',
        section: 'maintenance',
        label: 'Staff arrangement',
        type: 'radio',
        options: [
            { value: 'full-onsite', label: 'Full-time on-site' },
            { value: 'part-onsite', label: 'Part-time on-site' },
            { value: 'offsite', label: 'Off-site / as-needed' },
        ],
        showIf: (_c, a) => a['maintenance.hasStaff'] === 'yes',
    },
    {
        id: 'maintenance.majorConstructionPlanned',
        section: 'maintenance',
        label: 'Any major construction or renovation planned?',
        type: 'yesno',
        options: yesNoOptions,
    },
    {
        id: 'maintenance.writtenInspectionProcedures',
        section: 'maintenance',
        label: 'Written procedures for premises inspection?',
        type: 'yesno',
        options: yesNoOptions,
    },
    {
        id: 'maintenance.inspectionFreq',
        section: 'maintenance',
        label: 'Inspection frequency',
        type: 'radio',
        options: [
            { value: 'monthly', label: 'Monthly' },
            { value: 'quarterly', label: 'Quarterly' },
            { value: 'semiannually', label: 'Semi-annually' },
            { value: 'annually', label: 'Annually' },
        ],
        showIf: (_c, a) => a['maintenance.writtenInspectionProcedures'] === 'yes',
    },
    {
        id: 'maintenance.inspectionLogs',
        section: 'maintenance',
        label: 'Written logs kept of inspections, maintenance, and repairs?',
        type: 'yesno',
        options: yesNoOptions,
    },
    {
        id: 'maintenance.complaintProcedures',
        section: 'maintenance',
        label: 'Written procedures for responding to tenant complaints?',
        type: 'yesno',
        options: yesNoOptions,
    },
    {
        id: 'maintenance.snowIceRemoval',
        section: 'maintenance',
        label: 'Snow/ice removal procedure in place?',
        type: 'yesno',
        options: yesNoOptions,
    },
];
// --- Section 07 — Parking ----------------------------------------------
const parking = [
    {
        id: 'parking.hasParking',
        section: 'parking',
        label: 'Parking facilities on premises?',
        type: 'yesno',
        options: yesNoOptions,
    },
    {
        id: 'parking.type',
        section: 'parking',
        label: 'Parking type',
        type: 'radio',
        options: [
            { value: 'open-lot', label: 'Open lot' },
            { value: 'garage', label: 'Garage' },
            { value: 'both', label: 'Both' },
        ],
        showIf: (_c, a) => a['parking.hasParking'] === 'yes',
    },
    {
        id: 'parking.features',
        section: 'parking',
        label: 'Parking features',
        type: 'multiselect',
        options: [
            { value: 'lights', label: 'Lights' },
            { value: 'cameras', label: 'Cameras' },
            { value: 'guards', label: 'Guards' },
        ],
        showIf: (_c, a) => a['parking.hasParking'] === 'yes',
    },
    {
        id: 'parking.garageSqFt',
        section: 'parking',
        label: 'Garage square footage',
        type: 'number',
        showIf: (_c, a) => a['parking.type'] === 'garage' || a['parking.type'] === 'both',
    },
    {
        id: 'parking.residentTransport',
        section: 'parking',
        label: 'Vehicles used to transport residents?',
        type: 'yesno',
        options: yesNoOptions,
    },
    {
        id: 'parking.transportDesc',
        section: 'parking',
        label: 'Describe transport (type, capacity, frequency)',
        type: 'textarea',
        showIf: (_c, a) => a['parking.residentTransport'] === 'yes',
    },
];
// --- Section 08 — Recreation -------------------------------------------
const recreation = [
    // Pool — appears if classifier.features includes pool
    {
        id: 'recreation.hasPool',
        section: 'recreation',
        label: 'Swimming pool(s) on premises?',
        type: 'yesno',
        options: yesNoOptions,
        showIf: (c) => c.features.includes('pool'),
    },
    {
        id: 'recreation.numPools',
        section: 'recreation',
        label: 'Number of pools',
        type: 'number',
        showIf: (c, a) => c.features.includes('pool') && a['recreation.hasPool'] === 'yes',
    },
    {
        id: 'recreation.numHotTubs',
        section: 'recreation',
        label: 'Number of hot tubs',
        type: 'number',
        showIf: (c) => c.features.includes('spa') || c.features.includes('pool'),
    },
    {
        id: 'recreation.poolIndoorOutdoor',
        section: 'recreation',
        label: 'Indoor or outdoor?',
        type: 'radio',
        options: [
            { value: 'indoor', label: 'Indoor' },
            { value: 'outdoor', label: 'Outdoor' },
            { value: 'both', label: 'Both' },
        ],
        showIf: (c, a) => c.features.includes('pool') && a['recreation.hasPool'] === 'yes',
    },
    {
        id: 'recreation.poolFenced',
        section: 'recreation',
        label: 'Pool surrounded by fence/wall with self-closing gate?',
        type: 'yesno',
        options: yesNoOptions,
        showIf: (c, a) => c.features.includes('pool') && a['recreation.hasPool'] === 'yes',
    },
    {
        id: 'recreation.depthMarked',
        section: 'recreation',
        label: 'Depth clearly marked on top and inside edges?',
        type: 'yesno',
        options: yesNoOptions,
        showIf: (c, a) => c.features.includes('pool') && a['recreation.hasPool'] === 'yes',
    },
    {
        id: 'recreation.divingBoards',
        section: 'recreation',
        label: 'Diving boards or slides?',
        type: 'yesno',
        options: yesNoOptions,
        showIf: (c, a) => c.features.includes('pool') && a['recreation.hasPool'] === 'yes',
    },
    {
        id: 'recreation.poolMaintenance',
        section: 'recreation',
        label: 'Who maintains the pool?',
        type: 'radio',
        options: [
            { value: 'employee', label: 'Employee' },
            { value: 'outside-service', label: 'Outside service' },
        ],
        showIf: (c, a) => c.features.includes('pool') && a['recreation.hasPool'] === 'yes',
    },
    // Playground
    {
        id: 'recreation.playground',
        section: 'recreation',
        label: 'Playground facilities?',
        type: 'yesno',
        options: yesNoOptions,
        showIf: (c) => c.features.includes('playground'),
    },
    {
        id: 'recreation.playgroundSurface',
        section: 'recreation',
        label: 'Playground surface',
        type: 'radio',
        options: [
            { value: 'grass', label: 'Grass' },
            { value: 'sand', label: 'Sand' },
            { value: 'rubber', label: 'Rubber' },
            { value: 'other', label: 'Other' },
        ],
        showIf: (c, a) => c.features.includes('playground') &&
            a['recreation.playground'] === 'yes',
    },
    {
        id: 'recreation.playgroundInspections',
        section: 'recreation',
        label: 'Playground inspected and repaired periodically?',
        type: 'yesno',
        options: yesNoOptions,
        showIf: (c, a) => c.features.includes('playground') &&
            a['recreation.playground'] === 'yes',
    },
    // Clubhouse
    {
        id: 'recreation.clubhouseAlcohol',
        section: 'recreation',
        label: 'Alcohol permitted in clubhouse rentals?',
        type: 'yesno',
        options: yesNoOptions,
        showIf: (c) => c.features.includes('clubhouse'),
    },
    // Daycare
    {
        id: 'recreation.daycareLicensed',
        section: 'recreation',
        label: 'Daycare facility licensed?',
        type: 'yesno',
        options: yesNoOptions,
        showIf: (c) => c.features.includes('daycare'),
    },
];
// --- Section 09 — Commercial Tenants -----------------------------------
const commercialTenants = [
    {
        id: 'commercialTenants.exceedsHalf',
        section: 'commercialTenants',
        label: 'Commercial space exceeds 50% of total building area?',
        type: 'yesno',
        options: yesNoOptions,
    },
    {
        id: 'commercialTenants.sqFt',
        section: 'commercialTenants',
        label: 'Commercial square footage',
        type: 'number',
    },
    {
        id: 'commercialTenants.description',
        section: 'commercialTenants',
        label: 'Describe commercial tenants',
        type: 'textarea',
        placeholder: 'Restaurants, retail, office, etc.',
        required: true,
    },
    {
        id: 'commercialTenants.coiNamesApplicant',
        section: 'commercialTenants',
        label: 'Commercial tenants provide COI naming applicant as additional insured?',
        type: 'yesno',
        options: yesNoOptions,
    },
];
// --- Section 10 — Subcontractors ---------------------------------------
const subcontractors = [
    {
        id: 'subcontractors.usesSubs',
        section: 'subcontractors',
        label: 'Does the applicant subcontract any work?',
        type: 'yesno',
        options: yesNoOptions,
    },
    {
        id: 'subcontractors.description',
        section: 'subcontractors',
        label: 'Describe subcontracted services',
        type: 'textarea',
        showIf: (_c, a) => a['subcontractors.usesSubs'] === 'yes',
    },
    {
        id: 'subcontractors.formalContracts',
        section: 'subcontractors',
        label: 'Formal contracts in place with all subcontractors?',
        type: 'yesno',
        options: yesNoOptions,
        showIf: (_c, a) => a['subcontractors.usesSubs'] === 'yes',
    },
    {
        id: 'subcontractors.holdHarmless',
        section: 'subcontractors',
        label: 'Hold-harmless agreements in place?',
        type: 'yesno',
        options: yesNoOptions,
        showIf: (_c, a) => a['subcontractors.usesSubs'] === 'yes',
    },
    {
        id: 'subcontractors.obtainsCOI',
        section: 'subcontractors',
        label: 'Certificates of insurance obtained from all subcontractors?',
        type: 'yesno',
        options: yesNoOptions,
        showIf: (_c, a) => a['subcontractors.usesSubs'] === 'yes',
    },
    {
        id: 'subcontractors.namedAsAI',
        section: 'subcontractors',
        label: 'All subcontractors name applicant as additional insured?',
        type: 'yesno',
        options: yesNoOptions,
        showIf: (_c, a) => a['subcontractors.usesSubs'] === 'yes',
    },
];
// --- Section 11 — General Liability Exposure ---------------------------
const generalLiability = [
    {
        id: 'generalLiability.limitsDesired',
        section: 'generalLiability',
        label: 'Liability limits desired',
        type: 'select',
        required: true,
        options: [
            { value: '1m-2m', label: '$1M / $2M' },
            { value: '2m-4m', label: '$2M / $4M' },
            { value: '5m-5m', label: '$5M / $5M' },
            { value: 'other', label: 'Other (specify in notes)' },
        ],
    },
    {
        id: 'generalLiability.numAdditionalInsureds',
        section: 'generalLiability',
        label: 'Number of additional insureds required',
        type: 'number',
    },
    {
        id: 'generalLiability.lossOfIncomeLimit',
        section: 'generalLiability',
        label: 'Loss-of-income limit requested',
        type: 'currency',
    },
];
// --- Section 12 — Workers Comp -----------------------------------------
const workersComp = [
    {
        id: 'workersComp.estAnnualPayroll',
        section: 'workersComp',
        label: 'Estimated annual payroll',
        type: 'currency',
        required: true,
    },
    {
        id: 'workersComp.statesOperating',
        section: 'workersComp',
        label: 'States where employees work',
        type: 'multiselect',
        options: [], // populated from classifier.state + common states in UI
        helpText: 'Check every state where you have payroll exposure.',
    },
    {
        id: 'workersComp.priorCarrier',
        section: 'workersComp',
        label: 'Prior WC carrier',
        type: 'text',
    },
    {
        id: 'workersComp.priorExpMod',
        section: 'workersComp',
        label: 'Prior experience modification factor',
        type: 'text',
        placeholder: '1.00',
    },
    {
        id: 'workersComp.classifiedEmployeeClasses',
        section: 'workersComp',
        label: 'Describe employee duties and classes',
        type: 'textarea',
        placeholder: 'e.g. property management (8742), maintenance (9015), groundskeeping (0042)',
    },
];
// --- Section 13 — Excess / Umbrella ------------------------------------
const excessUmbrella = [
    {
        id: 'excessUmbrella.limitDesired',
        section: 'excessUmbrella',
        label: 'Umbrella limit desired',
        type: 'select',
        required: true,
        options: [
            { value: '5m', label: '$5M' },
            { value: '10m', label: '$10M' },
            { value: '25m', label: '$25M' },
            { value: '50m', label: '$50M' },
            { value: 'other', label: 'Other (specify in notes)' },
        ],
    },
    {
        id: 'excessUmbrella.underlyingScheduleNotes',
        section: 'excessUmbrella',
        label: 'Underlying schedule — notes',
        type: 'textarea',
        helpText: 'List underlying policies (GL, Auto, Employers Liability) + carriers and limits if known.',
    },
];
// --- Section 14 — Loss History -----------------------------------------
const lossHistory = [
    {
        id: 'lossHistory.claimsLast5Years',
        section: 'lossHistory',
        label: 'Claims made against the applicant in the last 5 years?',
        type: 'yesno',
        options: yesNoOptions,
        required: true,
    },
    {
        id: 'lossHistory.assaultMolestationClaims',
        section: 'lossHistory',
        label: 'Any assault, molestation, rape, burglary, or murder claims in the past 5 years?',
        type: 'yesno',
        options: yesNoOptions,
        required: true,
    },
    {
        id: 'lossHistory.leadClaims',
        section: 'lossHistory',
        label: 'Any prior lead claims?',
        type: 'yesno',
        options: yesNoOptions,
        required: true,
    },
    {
        id: 'lossHistory.moldClaims',
        section: 'lossHistory',
        label: 'Any prior mold claims?',
        type: 'yesno',
        options: yesNoOptions,
        required: true,
    },
    {
        id: 'lossHistory.largeLossDescription',
        section: 'lossHistory',
        label: 'Describe any losses of $50,000 and above',
        type: 'textarea',
        placeholder: 'Date, type of claim, description, amount',
    },
];
// --- Composed catalog --------------------------------------------------
export const QUESTIONS = [
    ...applicant,
    ...building,
    ...occupancy,
    ...lifeSafety,
    ...security,
    ...maintenance,
    ...parking,
    ...recreation,
    ...commercialTenants,
    ...subcontractors,
    ...generalLiability,
    ...workersComp,
    ...excessUmbrella,
    ...lossHistory,
];
// Exports useful to tests and other modules
export { applicant, building, occupancy, lifeSafety, security, maintenance, parking, recreation, commercialTenants, subcontractors, generalLiability, workersComp, excessUmbrella, lossHistory, };
//# sourceMappingURL=questions.js.map